import React, { createContext, useContext, useState, ReactNode, useMemo } from 'react';
import { ReturnQuery } from '../../../services/query';

interface LostCargoReturnPageContextType {
    currentPage: number;
    searchKeyword: string;
    returnListData: any;
    returnReason: any[];
    searchTrigger: number;
    selectedProducts: Map<number, number>;
    setReturnListData: (data: any[]) => void;
    setCurrentPage: (page: number) => void;
    setSearchKeyword: (keyword: string) => void;
    setSearchTrigger: (value: number | ((prev: number) => number)) => void;
    handleSearch: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleQuantityChange: (barcode: number, quantity: number) => void;
    selectAllProducts: (products: Array<{ barcode: number; quantity: number }>) => void;
    resetSelectedProducts: () => void;
}

interface LostCargoReturnPageProviderProps {
    children: ReactNode;
}

const LostCargoReturnPageContext = createContext<LostCargoReturnPageContextType | undefined>(undefined);

export const LostCargoReturnPageProvider: React.FC<LostCargoReturnPageProviderProps> = ({ children }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [returnListData, setReturnListData] = useState<any>([]);
    const [returnReason, setReturnReason] = useState<any[]>([]);
    const [searchTrigger, setSearchTrigger] = useState(0);
    const [selectedProducts, setSelectedProducts] = useState<Map<number, number>>(new Map());

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchKeyword(event.target.value);
    };

    const GetReturnReason = () => {
        ReturnQuery.GetReturnReason().then((res: any) => {
            setReturnReason(res.data);
        });
    };

    const handleQuantityChange = (barcode: number, quantity: number) => {
        setSelectedProducts((prev) => {
            const newMap = new Map(prev);
            newMap.set(barcode, quantity);
            return newMap;
        });
    };

    const selectAllProducts = (products: Array<{ barcode: number; quantity: number }>) => {
        const newMap = new Map();
        products.forEach((product) => {
            newMap.set(product.barcode, product.quantity);
        });
        setSelectedProducts(newMap);
    };

    const resetSelectedProducts = () => {
        setSelectedProducts(new Map());
    };

    const value = useMemo(() => ({
        currentPage,
        searchKeyword,
        returnListData,
        returnReason,
        searchTrigger,
        selectedProducts,
        setReturnListData,
        setCurrentPage,
        setSearchKeyword,
        setSearchTrigger,
        handleSearch,
        GetReturnReason,
        handleQuantityChange,
        selectAllProducts,
        resetSelectedProducts
    }), [
        currentPage,
        searchKeyword,
        returnListData,
        returnReason,
        searchTrigger,
        selectedProducts
    ]);

    return (
        <LostCargoReturnPageContext.Provider value={value}>
            {children}
        </LostCargoReturnPageContext.Provider>
    );
};

export const useLostCargoReturnPage = () => {
    const context = useContext(LostCargoReturnPageContext);
    if (context === undefined) {
        throw new Error('useLostCargoReturnPage must be used within a LostCargoReturnPageProvider');
    }
    return context;
}; 